import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../../ui/dialog";
import React, { useEffect, useState } from "react";
import Button from "../../form/button";
import ErrorMessage from "../../form/errorMessage";
import { IExpenseList, IExpenseListRecord, Status } from "../../../lib/types";
import { Input } from "../../ui/input";
import pb from "../../../lib/pocketbase";
import { useNavigate } from "react-router-dom";
import { IExpenseListWithTotalRecord } from "../../../pages/studio/Expenses/Lists";
import { Switch } from "../../ui/switch";
import { config } from "../../../config";
import { useToast } from "../../ui/use-toast";
import { createSlug } from "../../../lib/utils";

interface EditListProps {
    list?: IExpenseListWithTotalRecord;
    children: React.ReactNode;
    reload?: () => void;
}

export default function EditList({ reload, list, children }: EditListProps) {
    const navigate = useNavigate();
    const [name, setName] = useState("");
    const [isPublic, setIsPublic] = useState(false);
    const [isArchived, setIsArchived] = useState(false);
    const [open, setOpen] = useState(false);
    const [error, setError] = useState("");
    const [status, setStatus] = useState<Status>("none");
    const { toast } = useToast();

    function edit() {
        if (!pb.authStore.model || !list) return;
        setStatus("loading");
        const data: IExpenseList = {
            name: name,
            user: pb.authStore.model.id,
            public: isPublic,
            archive: isArchived
        };

        pb.collection("expenseList")
            .update(list.id, data)
            .then((x) => {
                reload && reload();
                setTimeout(() => {
                    setStatus("success");
                    setError("");
                    setOpen(false);
                    navigate(`/studio/expenses/list/${x.id}`);
                }, 500);
            })
            .catch((err: Error) => {
                setStatus("error");
                setError(err.message);
            })
            .finally(() => {
                setTimeout(() => {
                    setStatus("none");
                }, 3000);
            });
    }

    function copyUrl() {
        navigator.clipboard?.writeText(`${config.base}/expenses/${createSlug(list?.name || "")}/${list?.id}`);
        toast({
            title: "🤩 Successfully copied url!",
            description: "You have successfully copied the url of this expenses list."
        });
    }

    useEffect(() => {
        setName(list?.name || "");
        setIsPublic(list?.public || false);
        setIsArchived(list?.archive || false);
    }, [list]);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>{children}</DialogTrigger>
            <DialogContent className="mt-8 md:mt-0">
                <DialogHeader>
                    <DialogTitle>Edit list</DialogTitle>
                </DialogHeader>
                <ErrorMessage error={error} close={() => setError("")} />
                <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && edit()} placeholder="List name" />

                <p className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        Public: <Switch checked={isPublic} onCheckedChange={setIsPublic} />
                    </div>
                    {isPublic && <Button onClick={copyUrl}>Copy url</Button>}
                </p>
                <p className="flex items-center gap-2">
                    Archive: <Switch checked={isArchived} onCheckedChange={setIsArchived} />
                </p>
                <DialogFooter className="gap-2">
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button status={status} onClick={() => edit()} variant="dark">
                        {status === "loading" && "Editing"}
                        {status === "success" && "Edited"}
                        {status === "error" && "Error"}
                        {status === "none" && "Edit"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
